import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';
import { CheckCircle, XCircle, Clock, Building2, Send, Edit3 } from 'lucide-react';

const fmtPercent = (n) => {
    const num = Number(n);
    return num % 1 === 0 ? `${num}٪` : `${num.toFixed(1)}٪`;
};

export default function VendorContractsPage() {
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Chat & Edit State
    const [activeChatId, setActiveChatId] = useState(null);
    const [chatMessage, setChatMessage] = useState('');
    const [editingDiscountId, setEditingDiscountId] = useState(null);
    const [newDiscount, setNewDiscount] = useState(0);

    const loadContracts = async () => {
        setLoading(true);
        try {
            const { data, error } = await apiClient.GET('/api/v1/vendor/contracts');
            if (error) throw new Error(error.detail || 'خطا در دریافت اطلاعات');
            setContracts(data || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadContracts();
    }, []);

    const handleApprove = async (id) => {
        if (!window.confirm('آیا از تایید این قرارداد اطمینان دارید؟')) return;
        try {
            await apiClient.POST(`/api/v1/vendor/contracts/${id}/vendor-approve`);
            alert('قرارداد تایید شد و برای بررسی نهایی به مدیریت ارسال شد.');
            loadContracts();
        } catch (e) {
            alert(e.message);
        }
    };

    const handleReject = async (id) => {
        const reason = prompt('دلیل لغو/رد قرارداد را وارد کنید:');
        if (!reason) return;
        try {
            await apiClient.POST(`/api/v1/vendor/contracts/${id}/vendor-reject`, { body: { rejection_reason: reason } });
            alert('قرارداد لغو شد.');
            loadContracts();
        } catch (e) {
            alert(e.message);
        }
    };

    const handleUpdateDiscount = async (id) => {
        try {
            await apiClient.PATCH(`/api/v1/vendor/contracts/${id}/discount`, { body: { discount_percent: Number(newDiscount) } });
            alert('درصد تخفیف با موفقیت به‌روزرسانی شد.');
            setEditingDiscountId(null);
            loadContracts();
        } catch (e) {
            alert(e.message);
        }
    };

    const handleSendMessage = async (contractId) => {
        if (!chatMessage.trim()) return;
        try {
            await apiClient.POST(`/api/v1/vendor/contracts/${contractId}/messages`, { body: { message: chatMessage } });
            setChatMessage('');
            loadContracts();
        } catch (e) {
            alert(e.message);
        }
    };

    if (loading) return <div style={{ textAlign: 'center', padding: '3rem' }}>در حال دریافت اطلاعات...</div>;

    return (
        <div style={{ direction: 'rtl', width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            <header style={{ marginBottom: '0' }}>
                <h1 style={{ fontSize: '1.25rem', color: '#1e293b', margin: 0 }}>قراردادهای فروش گروهی کالا</h1>
            </header>

            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '0.75rem', borderRadius: '8px', fontSize: '0.9rem' }}>{error}</div>}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                {contracts.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '3rem', background: '#fff', borderRadius: '12px', border: '1px dashed #cbd5e1', color: '#64748b' }}>
                        هیچ قراردادی یافت نشد.
                    </div>
                )}

                {contracts.map(c => {
                    const isPendingVendor = !c.vendor_approved_at && c.status !== 'rejected';
                    const isApproved = c.vendor_approved_at;
                    const isRejected = c.status === 'rejected';
                    const isChatOpen = activeChatId === c.id;

                    return (
                        <div key={c.id} style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>

                            {/* ردیف بالا */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>

                                {/* سمت راست: کد و تخفیف */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                        <Building2 size={16} color="#64748b" />
                                        <span style={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#1e293b' }}>کد: {c.company_code}</span>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: '#f8fafc', padding: '0.2rem 0.5rem', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>تخفیف:</span>
                                        {editingDiscountId === c.id ? (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                                                <input type="number" value={newDiscount} onChange={e => setNewDiscount(e.target.value)} style={{ width: '50px', padding: '2px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.75rem', textAlign: 'center' }} />
                                                <button onClick={() => handleUpdateDiscount(c.id)} style={{ background: '#10b981', color: '#fff', border: 'none', padding: '2px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem' }}>ذخیره</button>
                                            </span>
                                        ) : (
                                            <>
                                                <strong style={{ color: '#ef4444', fontSize: '0.85rem' }}>{c.discount_percent ? fmtPercent(c.discount_percent) : '-'}</strong>
                                                {isPendingVendor && (
                                                    <button onClick={() => { setEditingDiscountId(c.id); setNewDiscount(Number(c.discount_percent)); }} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}>
                                                        <Edit3 size={12} />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* سمت چپ: وضعیت و دکمه‌ها */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    {isApproved && !isRejected && <span style={{ background: '#dcfce7', color: '#15803d', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold' }}>تایید شده</span>}
                                    {isRejected && <span style={{ background: '#fee2e2', color: '#b91c1c', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold' }}>لغو شده</span>}

                                    <button onClick={() => setActiveChatId(isChatOpen ? null : c.id)} style={{ background: '#f8fafc', color: '#475569', border: '1px solid #cbd5e1', padding: '0.3rem 0.6rem', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem' }}>
                                        💬 پیام‌ها ({c.messages?.length || 0})
                                    </button>

                                    {isPendingVendor && (
                                        <>
                                            <button onClick={() => handleReject(c.id)} style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', padding: '0.3rem 0.6rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.75rem' }}>لغو</button>
                                            <button onClick={() => handleApprove(c.id)} style={{ background: '#10b981', color: '#fff', border: 'none', padding: '0.3rem 0.6rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.75rem' }}>تایید</button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* ردیف وسط: اقلام */}
                            <div style={{ background: '#f8fafc', padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 'bold' }}>اقلام:</span>
                                {c.basket_items?.map(item => (
                                    <span key={item.id} style={{ background: '#fff', border: '1px solid #cbd5e1', padding: '0 0.4rem', borderRadius: '4px', fontSize: '0.75rem', color: '#1e293b' }}>
                                        {item.product_name || 'کالا'} (× {item.max_qty || 1})
                                    </span>
                                ))}
                            </div>

                            {/* دلیل لغو */}
                            {isRejected && c.rejection_reason && (
                                <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '0.4rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', border: '1px solid #fecaca' }}>
                                    <strong>دلیل لغو:</strong> {c.rejection_reason}
                                </div>
                            )}

                            {/* چت */}
                            {isChatOpen && (
                                <div style={{ background: '#f8fafc', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', marginTop: '0.25rem' }}>
                                    <div style={{ maxHeight: '150px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.5rem' }}>
                                        {(!c.messages || c.messages.length === 0) ? (
                                            <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>پیامی وجود ندارد.</div>
                                        ) : c.messages.map(m => (
                                            <div key={m.id} style={{ alignSelf: m.sender_role === 'vendor' ? 'flex-start' : 'flex-end', maxWidth: '80%', background: m.sender_role === 'vendor' ? '#dcfce7' : '#e0f2fe', padding: '0.3rem 0.6rem', borderRadius: '6px', fontSize: '0.8rem' }}>
                                                <div style={{ fontSize: '0.65rem', color: '#64748b', marginBottom: '0.1rem' }}>
                                                    {m.sender_role === 'super_admin' ? 'مدیر' : m.sender_role === 'vendor' ? 'شما' : 'شرکت'}
                                                </div>
                                                {m.message}
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                                        <input type="text" value={chatMessage} onChange={e => setChatMessage(e.target.value)} placeholder="پیام..." style={{ flex: 1, padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem', outline: 'none' }} onKeyDown={e => e.key === 'Enter' && handleSendMessage(c.id)} />
                                        <button onClick={() => handleSendMessage(c.id)} style={{ background: '#10b981', color: '#fff', border: 'none', padding: '0 0.75rem', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Send size={14} /></button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}